import type { BrandConfig } from '@estimating/branding';
import type { Locale } from '@estimating/i18n';
import dayjs from 'dayjs';
import type { i18n as I18n } from 'i18next';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { LocaleContext, type LocaleContextValue } from '../locale/locale-context';
import { dayjsLocales } from '../locale/locale-packs';
import type { LocalePreferenceStore } from '../locale/locale-preference';

interface Props {
  readonly brand: BrandConfig;
  readonly i18n: I18n;
  readonly initialLocale: Locale;
  readonly preferences: LocalePreferenceStore;
  readonly children: ReactNode;
}

/** Owns the current locale and keeps i18next, Day.js, the page language and the stored choice in step. */
export function LocaleProvider({ brand, i18n, initialLocale, preferences, children }: Props) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    dayjs.locale(dayjsLocales[locale]);
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      supportedLocales: brand.supportedLocales,
      setLocale: (next) => {
        void i18n.changeLanguage(next).then(() => {
          preferences.write(next);
          setLocaleState(next);
        });
      },
    }),
    [brand.supportedLocales, i18n, locale, preferences],
  );

  return <LocaleContext value={value}>{children}</LocaleContext>;
}
